import { useTranslate } from '@ur-solutions/react-hooks'
import type { TableMenuItem } from '@/components_deprecated'
import {
  Table as BaseTable,
  SortableHeader,
  TableMenu,
} from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { differenceInDays, format, parseISO } from 'date-fns'
import { ActivitiesStatus } from '@/modules/activities/components/overview/ActivitiesStatusCell'
import type { VehicleNode } from '@/modules/vehicles'
import type { VehicleFilter } from '@/modules/vehicles/types'
import type {
  VehicleNote,
  VehicleOrdering,
} from '@/modules/vehicles/types.graphql'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled, { useTheme } from 'styled-components'
import { useAdmin, useConfirm, usePermissions } from '@/utils/hooks'
import { useVehicleMutations } from '../mutations.hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { getDisplayStringFromVehicleFetchInfoEndpoint } from '../util'
import { safeParseJson } from '@/utils'
import type { Op } from 'quill-delta'
import Delta from 'quill-delta'
import truncate from 'lodash/truncate'

const Table = styled(BaseTable)`
  position: relative;

  thead {
    text-align: left;
  }

  tbody tr {
    &:hover {
      background: ${props => props.theme.colors.gray10};
    }
  }
`

const VehicleStatusSpan = styled.span`
  display: flex;
`

interface FleetTableProps {
  vehicles: VehicleNode[]
  filter: VehicleFilter
  onSort: (value: VehicleOrdering) => void
}

export const FleetTable: React.FC<FleetTableProps> = ({
  vehicles,
  filter,
  onSort,
}) => {
  const translations = useTranslate({
    vin: 'vehicles.vin',
    department: 'common.department',
    facility: 'common.facility',
    regNr: 'common.reg-nr',
    model: 'vehicles.model',
    type: 'common.type',
    brand: 'vehicles.brand',
    note: 'common.note',
    menu: {
      edit: 'vehicles.edit-vehicle',
      viewDetails: 'vehicles.view-vehicle-details',
      deleteVehicle: 'vehicles.delete-vehicle',
      data: 'common.data',
    },
    prompt: {
      delete: {
        title: 'vehicles.delete-vehicle-title',
        message: 'vehicles.delete-vehicle-message',
      },
    },
  })

  const navigate = useNavigate()

  const theme = useTheme()
  const admin = useAdmin()
  const confirm = useConfirm()
  const {
    mutations: { delete: deleteVehicle },
  } = useVehicleMutations()
  const { hasPermissions } = usePermissions()
  const canDelete = hasPermissions(PERMISSIONS.vehicles.delete.vehicle)

  async function handleDeleteVehicle(id: string) {
    const promptTitle = translations.prompt.delete.title
    const promptMessage = translations.prompt.delete.message

    const { data: answer } = await confirm(promptMessage, promptTitle)
    if (!answer) return

    void deleteVehicle({
      variables: { id },
    })
  }

  const trancateNote = (notes?: VehicleNote[]): string => {
    if (!notes || notes.length === 0) return '-'
    const lastNote = notes[notes.length - 1]
    if (!lastNote) return '-'
    const delta = new Delta(safeParseJson<Op[]>(lastNote.text, []))
    const noteText = delta.ops
      .map((op: Op) => {
        if (typeof op.insert === 'string') {
          return op.insert
        }
        return ''
      })
      .join('')
    return truncate(noteText, { length: 40 })
  }

  function createMenuItems(vehicle: VehicleNode) {
    const items: TableMenuItem<VehicleNode>[] = []
    if (admin)
      items.push({
        label: translations.menu.viewDetails,
        role: 'link',
        onClick: () => void navigate(`/vehicle-fleet/${vehicle.id}`),
      })
    if (canDelete) {
      items.push({
        label: translations.menu.deleteVehicle,
        onClick: () => handleDeleteVehicle(vehicle.id),
      })
    }

    return items
  }

  return (
    <>
      <Table>
        <thead>
          <tr>
            <SortableHeader
              baseValue="registrationNumber"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.regNr}
            </SortableHeader>

            <SortableHeader
              baseValue="vin"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.vin}
            </SortableHeader>

            <SortableHeader
              baseValue="brand"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.brand}
            </SortableHeader>

            <SortableHeader
              baseValue="model"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.model}
            </SortableHeader>

            <SortableHeader
              baseValue="vehicleType"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.type}
            </SortableHeader>
            <SortableHeader
              baseValue="department__name"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.department}
            </SortableHeader>
            <SortableHeader
              baseValue="facility__name"
              sortValue={filter.orderBy}
              onSort={onSort}
            >
              {translations.facility}
            </SortableHeader>
            <th>{translations.note}</th>
            {admin && !isMobile && <th>{translations.menu.data}</th>}
            <th></th>
          </tr>
        </thead>

        <tbody>
          {vehicles.map((vehicle, vehicleIndex) => {
            const menuItems = createMenuItems(vehicle)

            return (
              <tr key={vehicle.vin}>
                <td>
                  <PlainLink
                    to={`/vehicle-fleet/${vehicle.id}`}
                    $hoverColor="secondary"
                  >
                    {vehicle.registrationNumber}
                  </PlainLink>
                </td>
                <td>{vehicle.vin}</td>
                <td>{vehicle.brand}</td>
                <td>{vehicle.model}</td>
                <td>{vehicle.vehicleType}</td>
                <td>{vehicle.department?.name ?? '-'}</td>
                <td>{vehicle.facility?.name ?? '-'}</td>
                <td>{trancateNote(vehicle.vehicleNotes)}</td>
                {admin && !isMobile && (
                  <td>
                    {vehicle.vehicleFetchInfo.map(vehicleFetchInfo => {
                      if (!vehicleFetchInfo.lastFetch) return null

                      const fetchDate = parseISO(vehicleFetchInfo.lastFetch)
                      const fetchDifference = differenceInDays(
                        new Date(),
                        fetchDate
                      )
                      const color =
                        fetchDifference < 7
                          ? theme.colors.green
                          : fetchDifference < 10
                          ? theme.colors.orange
                          : theme.colors.red
                      const tooltipProps =
                        vehicleIndex === vehicles.length - 1
                          ? { top: '-150%' }
                          : undefined

                      const endpoint =
                        getDisplayStringFromVehicleFetchInfoEndpoint(
                          vehicleFetchInfo
                        )

                      return (
                        <VehicleStatusSpan key={vehicleFetchInfo.id}>
                          <ActivitiesStatus
                            dataStatusColor={color}
                            tooltip={format(fetchDate, 'HH:mm dd.MM.yyyy')}
                            tooltipProps={tooltipProps}
                          />
                          {endpoint}
                        </VehicleStatusSpan>
                      )
                    })}
                  </td>
                )}
                <TableMenu items={menuItems} />
              </tr>
            )
          })}
        </tbody>
      </Table>
    </>
  )
}
